
/**
 * FormTemplate Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useSystemSurveysStore } from '@/store/system-forms.store';
import { useTenantStore } from '@/store/tenant.store';
import { hydrationManager } from '@/lib/services/reference-hydration.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
const FORM_TEMPLATE_REF_KEY = 'formTemplate';

function refreshFormTemplateReference() {
    hydrationManager.refreshEntity(FORM_TEMPLATE_REF_KEY, { force: true });
}

export class FormTemplateIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _refetchCurrentSurveys() {
        const { searchParams, columnFilters } = useSystemSurveysStore.getState();
        return this.fetchSurveys(
            searchParams.page || 1,
            searchParams.pageSize || 10,
            searchParams.searchTerm || '',
            searchParams.sortBy || 'createdAt',
            searchParams.sortDirection || 'desc',
            columnFilters || {},
        );
    }

    /**
     * Fetch surveys and update store
     */
    async fetchSurveys(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', columnFilters = {}) {
        const store = useSystemSurveysStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setSurveys(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create survey (form template)
     */
    async createSurvey(data) {
        const store = useSystemSurveysStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const payload = { ...data, tenantId };
            const response = await this.service.create(payload);

            toast.success(i18next.t('definitionsSurveys:messages.createSuccess', {
                defaultValue: 'Survey created',
            }));

            if (store.closeSurveyBuilder) store.closeSurveyBuilder();
            refreshFormTemplateReference();
            await this._refetchCurrentSurveys();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update survey
     */
    async updateSurvey(id, data) {
        const store = useSystemSurveysStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('definitionsSurveys:messages.updateSuccess', {
                defaultValue: 'Survey updated',
            }));

            if (store.closeSurveyBuilder) store.closeSurveyBuilder();
            refreshFormTemplateReference();
            await this._refetchCurrentSurveys();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete survey
     */
    async deleteSurvey(id) {
        const store = useSystemSurveysStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('definitionsSurveys:messages.deleteSuccess', {
                defaultValue: 'Survey deleted',
            }));
            refreshFormTemplateReference();
            await this._refetchCurrentSurveys();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete surveys
     */
    async bulkDeleteSurveys(ids) {
        const store = useSystemSurveysStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.bulkDelete(ids);

            toast.success(i18next.t('definitionsSurveys:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} surveys deleted`,
            }));
            refreshFormTemplateReference();
            await this._refetchCurrentSurveys();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch a single form/survey template by id (for embedded survey fill).
     */
    async fetchTemplateById(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getById(id);
            const data = response?.data ?? response;
            return { success: true, data };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /** Published surveys for vendor assessment picker */
    async fetchPublishedSurveyTemplates() {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getAll({
                page: 1,
                pageSize: 500,
                tenantId,
                sortBy: 'name',
                sortOrder: 'asc',
                filter_templateKind: 'survey',
                filter_status: 'published',
            });
            return { success: true, data: response.data || [] };
        } catch (error) {
            return { success: false, data: [], error: this._getErrorMessage(error) };
        }
    }

    /**
     * Surveys for workflow FORM_FILL picker — same catalog as /definitions/surveys
     * (all statuses; API defaults templateKind=survey).
     */
    async fetchSurveysForWorkflow() {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getAll({
                page: 1,
                pageSize: 500,
                tenantId,
                sortBy: 'name',
                sortOrder: 'asc',
                filter_templateKind: 'survey',
            });
            return { success: true, data: response.data || [] };
        } catch (error) {
            return { success: false, data: [], error: this._getErrorMessage(error) };
        }
    }

    /** @deprecated Prefer fetchSurveysForWorkflow — kept for single-select callers */
    async fetchPublishedFormTemplatesForWorkflow() {
        return this.fetchSurveysForWorkflow();
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
